import { z } from 'zod/v3';
import { IntegrationTypeSchema } from '../generated/enums';

// Integration DTOs
export const IntegrationDTOSchema = z.object({
  id: z.string(),
  name: z.string(),
  type: IntegrationTypeSchema,
  workspaceId: z.string(),
  workspace: z.string(),
  config: z.record(z.unknown()).optional(),
  metadata: z.record(z.unknown()).optional(),
  status: z.string(),
  sources: z.array(z.string()),
  createdAt: z.date(),
  updatedAt: z.date()
}).strict();

export const IntegrationListDTOSchema = z.object({
  id: z.string(),
  name: z.string(),
  type: IntegrationTypeSchema,
  status: z.string(),
  createdAt: z.date(),
  updatedAt: z.date()
}).strict();

export const CreateIntegrationDTOSchema = z.object({
  name: z.string(),
  type: IntegrationTypeSchema,
  workspaceId: z.string(),
  workspace: z.string(),
  config: z.record(z.unknown()).optional(),
  metadata: z.record(z.unknown()).optional(),
  status: z.string(),
  sources: z.array(z.string())
}).strict();

export const UpdateIntegrationDTOSchema = z.object({
  id: z.string(),
  name: z.string().optional(),
  type: IntegrationTypeSchema.optional(),
  workspaceId: z.string().optional(),
  workspace: z.string().optional(),
  config: z.record(z.unknown()).optional(),
  metadata: z.record(z.unknown()).optional(),
  status: z.string().optional(),
  sources: z.array(z.string()).optional()
}).strict();

/**
 * #1842: the auth-failure circuit breaker's view of one provider credential.
 * `disabled` means scheduled checks are being skipped and a reconnect is
 * needed; a non-disabled row with `errorCode` (e.g. permission_denied) means
 * checks run but some are refused.
 */
export const AgentIntegrationHealthSchema = z.object({
  connectionId: z.string(),
  disabled: z.boolean(),
  errorCode: z.string().nullable(),
  lastError: z.string().nullable(),
  failingSince: z.string().nullable(),
  lastFailureAt: z.string().nullable(),
}).strict();

export const AgentIntegrationProviderStatusSchema = z.object({
  slug: z.string(),
  displayName: z.string(),
  state: z.enum(['live', 'connect-only']),
  provider: z.string(),
  connected: z.boolean(),
  /** True when the credential's breaker is tripped: connected on paper, dead in practice. */
  blocked: z.boolean(),
  health: AgentIntegrationHealthSchema.nullable(),
  canScan: z.boolean(),
  portalUrl: z.string().url(),
}).strict();

export const AgentIntegrationStatusResponseSchema = z.object({
  connectedCount: z.number().int().nonnegative(),
  /** Connected providers whose breaker is tripped (#1842). */
  blockedCount: z.number().int().nonnegative(),
  reconciling: z.boolean().nullable(),
  providers: z.array(AgentIntegrationProviderStatusSchema),
}).strict();

export const AgentIntegrationScanResultSchema = z.object({
  slug: z.string(),
  provider: z.string(),
  connectionId: z.string().nullable(),
  status: z.enum(['queued', 'reused', 'skipped', 'failed']),
  runId: z.string().optional(),
  plannedTotal: z.number().int().nonnegative().optional(),
  message: z.string().optional(),
}).strict();

export const AgentIntegrationScanResponseSchema = z.object({
  requestedProvider: z.string().nullable(),
  queued: z.number().int().nonnegative(),
  reused: z.number().int().nonnegative(),
  skipped: z.number().int().nonnegative(),
  failed: z.number().int().nonnegative(),
  results: z.array(AgentIntegrationScanResultSchema),
}).strict();

export type IntegrationDTO = z.infer<typeof IntegrationDTOSchema>;
export type IntegrationListDTO = z.infer<typeof IntegrationListDTOSchema>;
export type CreateIntegrationDTO = z.infer<typeof CreateIntegrationDTOSchema>;
export type UpdateIntegrationDTO = z.infer<typeof UpdateIntegrationDTOSchema>;
export type AgentIntegrationHealth = z.infer<typeof AgentIntegrationHealthSchema>;
export type AgentIntegrationProviderStatus = z.infer<typeof AgentIntegrationProviderStatusSchema>;
export type AgentIntegrationStatusResponse = z.infer<typeof AgentIntegrationStatusResponseSchema>;
export type AgentIntegrationScanResult = z.infer<typeof AgentIntegrationScanResultSchema>;
export type AgentIntegrationScanResponse = z.infer<typeof AgentIntegrationScanResponseSchema>;


export const AikidoConnectionStatusDTOSchema = z.object({
  integrationId: z.string().nullable(),
  disconnectPending: z.boolean(),
  cleanupPending: z.boolean(),
  oauthConfigured: z.boolean(),
  requiresReauthorization: z.boolean(),
  connectionId: z.string().nullable(),
  sourceWorkspaceId: z.number().nullable(),
  enabled: z.boolean(),
  status: z.string().nullable(),
  lastSyncAt: z.coerce.date().nullable(),
  lastError: z.string().nullable(),
});
export type AikidoConnectionStatusDTO = z.infer<typeof AikidoConnectionStatusDTOSchema>;
